<template>
  <div class="space-y-6">
    <!-- Fila 1: Estado de Resultados (P&L) y Flujo de Caja -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <!-- Estado de Resultados (Ocupa 2 cols) -->
      <div class="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div class="flex items-center justify-between pb-4 mb-4 border-b border-gray-100">
          <div class="flex items-center gap-3">
            <div class="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
              <i class="fa fa-file-invoice-dollar text-base" />
            </div>
            <div>
              <h3 class="text-base font-bold text-gray-900">Estado de Resultados (P&L)</h3>
              <p class="text-xs text-gray-500">Desglose de ingresos, costos directos y utilidad del período</p>
            </div>
          </div>

          <a
            :href="getPdfUrl('profit')"
            target="_blank"
            class="px-3 py-1.5 bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm"
          >
            <i class="fa fa-file-pdf text-rose-600" />
            <span>PDF P&L</span>
          </a>
        </div>

        <div class="overflow-x-auto">
          <table class="w-full text-left text-xs">
            <tbody class="divide-y divide-gray-100">
              <!-- Ventas Brutas -->
              <tr class="hover:bg-gray-50/50">
                <td class="py-3 px-2 font-semibold text-gray-800">
                  <div class="flex items-center gap-2">
                    <span class="w-2 h-2 rounded-full bg-blue-400" />
                    <span>Ingresos Brutos por Ventas</span>
                  </div>
                </td>
                <td class="py-3 px-2 text-right font-mono font-bold text-gray-900">
                  {{ formatCurrency(profit.income_statement.gross_sales) }}
                </td>
                <td class="py-3 px-2 text-right text-gray-400 font-mono">100.0%</td>
              </tr>

              <!-- Descuentos -->
              <tr class="hover:bg-gray-50/50">
                <td class="py-2.5 px-2 text-gray-600 pl-6">
                  (-) Descuentos comerciales aplicados
                </td>
                <td class="py-2.5 px-2 text-right font-mono text-rose-600 font-semibold">
                  -{{ formatCurrency(profit.income_statement.discounts) }}
                </td>
                <td class="py-2.5 px-2 text-right text-gray-400 font-mono">
                  {{ getPct(profit.income_statement.discounts, profit.income_statement.gross_sales) }}%
                </td>
              </tr>

              <!-- Ventas Netas -->
              <tr class="bg-gray-50/75 font-bold">
                <td class="py-3 px-2 text-gray-900">
                  (=) Ventas Netas Facturadas
                </td>
                <td class="py-3 px-2 text-right font-mono text-gray-900">
                  {{ formatCurrency(profit.income_statement.net_sales) }}
                </td>
                <td class="py-3 px-2 text-right text-gray-600 font-mono">100.0%</td>
              </tr>

              <!-- Costo de Ventas (COGS) -->
              <tr class="hover:bg-gray-50/50">
                <td class="py-2.5 px-2 text-gray-600 pl-6">
                  (-) Costo de Mercancía Vendida (COGS)
                </td>
                <td class="py-2.5 px-2 text-right font-mono text-rose-600 font-semibold">
                  -{{ formatCurrency(profit.income_statement.cogs) }}
                </td>
                <td class="py-2.5 px-2 text-right text-gray-400 font-mono">
                  {{ getPct(profit.income_statement.cogs, profit.income_statement.net_sales) }}%
                </td>
              </tr>

              <!-- Utilidad Bruta -->
              <tr class="bg-emerald-50/60 font-bold border-t border-b border-emerald-100">
                <td class="py-3 px-2 text-emerald-900">
                  <div class="flex items-center gap-2">
                    <i class="fa fa-arrow-trend-up text-emerald-600" />
                    <span>(=) UTILIDAD BRUTA</span>
                  </div>
                </td>
                <td class="py-3 px-2 text-right font-mono text-emerald-700 text-sm">
                  {{ formatCurrency(profit.income_statement.gross_profit) }}
                </td>
                <td class="py-3 px-2 text-right text-emerald-800 font-mono">
                  {{ profit.income_statement.gross_margin_pct }}%
                </td>
              </tr>

              <!-- Gastos Operativos -->
              <tr class="hover:bg-gray-50/50">
                <td class="py-2.5 px-2 text-gray-600 pl-6">
                  (-) Gastos Operativos y Administrativos
                </td>
                <td class="py-2.5 px-2 text-right font-mono text-rose-600 font-semibold">
                  -{{ formatCurrency(profit.income_statement.expenses) }}
                </td>
                <td class="py-2.5 px-2 text-right text-gray-400 font-mono">
                  {{ getPct(profit.income_statement.expenses, profit.income_statement.net_sales) }}%
                </td>
              </tr>

              <!-- Utilidad Neta -->
              <tr
                class="font-extrabold text-sm border-t-2"
                :class="profit.income_statement.net_profit >= 0 ? 'bg-blue-50/70 border-blue-200 text-blue-900' : 'bg-rose-50/70 border-rose-200 text-rose-900'"
              >
                <td class="py-3.5 px-2">
                  <div class="flex items-center gap-2">
                    <i class="fa fa-trophy" :class="profit.income_statement.net_profit >= 0 ? 'text-blue-600' : 'text-rose-600'" />
                    <span>(=) UTILIDAD NETA DEL PERÍODO</span>
                  </div>
                </td>
                <td class="py-3.5 px-2 text-right font-mono">
                  {{ formatCurrency(profit.income_statement.net_profit) }}
                </td>
                <td class="py-3.5 px-2 text-right font-mono">
                  {{ profit.income_statement.net_margin_pct }}%
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Resumen de Flujo de Caja y Cobranzas (1 col) -->
      <div class="space-y-6">
        <!-- Balance de Caja -->
        <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div class="flex items-center gap-3 pb-3 mb-4 border-b border-gray-100">
            <div class="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <i class="fa fa-wallet text-sm" />
            </div>
            <div>
              <h3 class="text-sm font-bold text-gray-900">Flujo de Efectivo Operativo</h3>
              <p class="text-[11px] text-gray-400">Entradas vs Salidas reales de dinero</p>
            </div>
          </div>

          <div class="space-y-3 text-xs">
            <div class="flex justify-between items-center py-1.5 border-b border-gray-50">
              <span class="text-gray-600">(+) Cobros de Ventas</span>
              <span class="font-mono font-bold text-emerald-600">
                +{{ formatCurrency(stats.total_sales_collected) }}
              </span>
            </div>

            <div class="flex justify-between items-center py-1.5 border-b border-gray-50">
              <span class="text-gray-600">(-) Pagos a Proveedores</span>
              <span class="font-mono font-bold text-rose-600">
                -{{ formatCurrency(stats.total_purchases_paid) }}
              </span>
            </div>

            <div class="flex justify-between items-center py-1.5 border-b border-gray-50">
              <span class="text-gray-600">(-) Gastos Pagados</span>
              <span class="font-mono font-bold text-rose-600">
                -{{ formatCurrency(stats.total_expenses) }}
              </span>
            </div>

            <div class="pt-2 flex justify-between items-center text-sm font-extrabold">
              <span class="text-gray-800">Saldo Neto de Caja:</span>
              <span
                class="font-mono"
                :class="stats.cash_flow_balance >= 0 ? 'text-emerald-700' : 'text-rose-600'"
              >
                {{ formatCurrency(stats.cash_flow_balance) }}
              </span>
            </div>
          </div>
        </div>

        <!-- Gastos por Categoría -->
        <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 class="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">
            Desglose de Gastos Operativos
          </h3>

          <div v-if="profit.expenses_by_category && profit.expenses_by_category.length" class="space-y-2.5">
            <div
              v-for="cat in profit.expenses_by_category"
              :key="cat.category_id || cat.name"
              class="flex items-center justify-between text-xs"
            >
              <div class="flex items-center gap-2 truncate">
                <span class="w-1.5 h-1.5 rounded-full bg-rose-400" />
                <span class="font-medium text-gray-700 truncate">{{ cat.name }}</span>
              </div>
              <span class="font-mono font-bold text-gray-900 ml-2">
                {{ formatCurrency(cat.total) }}
              </span>
            </div>
          </div>
          <div v-else class="text-center py-6 text-gray-400 text-xs">
            <i class="fa fa-info-circle text-gray-300 text-base mb-1" />
            <div>Sin gastos registrados en el período.</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Fila 2: Top Productos Más Rentables vs Productos con Margen Bajo -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <!-- Más Rentables -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div class="flex items-center gap-2 mb-4">
          <i class="fa fa-medal text-amber-500 text-base" />
          <div>
            <h3 class="text-sm font-bold text-gray-900">Productos con Mayor Ganancia en $</h3>
            <p class="text-[11px] text-gray-400">Generan el mayor volumen de utilidad neta</p>
          </div>
        </div>

        <div class="overflow-x-auto">
          <table class="w-full text-xs text-left">
            <thead class="bg-gray-50 text-gray-500 uppercase font-bold text-[10px]">
              <tr>
                <th class="py-2 px-3">Producto</th>
                <th class="py-2 px-2 text-right">Venta</th>
                <th class="py-2 px-2 text-right">Ganancia</th>
                <th class="py-2 px-2 text-center">Margen</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr
                v-for="prod in profit.most_profitable_products"
                :key="prod.id"
                class="hover:bg-emerald-50/20"
              >
                <td class="py-2.5 px-3">
                  <div class="font-bold text-gray-900 truncate max-w-[200px]">{{ prod.name }}</div>
                  <div class="text-[10px] text-gray-400 font-mono">{{ prod.code }}</div>
                </td>
                <td class="py-2.5 px-2 text-right font-mono text-gray-700">
                  {{ formatCurrency(prod.total_revenue) }}
                </td>
                <td class="py-2.5 px-2 text-right font-mono font-bold text-emerald-600">
                  {{ formatCurrency(prod.profit) }}
                </td>
                <td class="py-2.5 px-2 text-center font-mono font-bold text-emerald-700">
                  {{ prod.margin_pct }}%
                </td>
              </tr>
              <tr v-if="!profit.most_profitable_products?.length">
                <td colspan="4" class="text-center py-4 text-gray-400">Sin datos de ventas</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Alertas de Margen Estrecho -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div class="flex items-center gap-2 mb-4">
          <i class="fa fa-exclamation-circle text-amber-500 text-base" />
          <div>
            <h3 class="text-sm font-bold text-gray-900">Productos con Menor Margen (%)</h3>
            <p class="text-[11px] text-gray-400">Revisar precios de venta o costos de compra</p>
          </div>
        </div>

        <div class="overflow-x-auto">
          <table class="w-full text-xs text-left">
            <thead class="bg-gray-50 text-gray-500 uppercase font-bold text-[10px]">
              <tr>
                <th class="py-2 px-3">Producto</th>
                <th class="py-2 px-2 text-right">Precio</th>
                <th class="py-2 px-2 text-right">Costo</th>
                <th class="py-2 px-2 text-center">Margen</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr
                v-for="prod in profit.lowest_margin_products"
                :key="prod.id"
                class="hover:bg-amber-50/20"
              >
                <td class="py-2.5 px-3">
                  <div class="font-bold text-gray-900 truncate max-w-[200px]">{{ prod.name }}</div>
                  <div class="text-[10px] text-gray-400 font-mono">{{ prod.code }}</div>
                </td>
                <td class="py-2.5 px-2 text-right font-mono text-gray-700">
                  {{ formatCurrency(prod.price) }}
                </td>
                <td class="py-2.5 px-2 text-right font-mono text-gray-500">
                  {{ formatCurrency(prod.cost) }}
                </td>
                <td class="py-2.5 px-2 text-center font-mono font-bold" :class="prod.margin_pct < 15 ? 'text-rose-600' : 'text-amber-600'">
                  {{ prod.margin_pct }}%
                </td>
              </tr>
              <tr v-if="!profit.lowest_margin_products?.length">
                <td colspan="4" class="text-center py-4 text-gray-400">Sin datos de ventas</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BalanceTab',

  props: {
    stats: {
      type: Object,
      required: true,
    },
    profit: {
      type: Object,
      required: true,
    },
    filters: {
      type: Object,
      required: true,
    },
  },

  methods: {
    getPdfUrl(type) {
      const from = this.filters.date_from || ''
      const to = this.filters.date_to || ''
      return `/reports/export/pdf?type=${type}&date_from=${from}&date_to=${to}`
    },

    formatCurrency(val) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
      }).format(val || 0)
    },

    getPct(part, total) {
      if (!total || total <= 0) return 0
      return ((part / total) * 100).toFixed(1)
    },
  },
}
</script>
